export const PAYMENT_METHOD_PAYPAL = 9;
export const PAYMENT_METHOD_NETPAY = 10;
export const PAYMENT_METHOD_STRIPE = 14;
export const PAYMENT_METHOD_AUTHORIZENET = 15;
export const PAYMENT_METHOD_MERCADOPAGO = 17;

export type SaleSlot = Record<string, unknown> & {
  date?: string | null;
  hour?: string | null;
};

export type SaleTherapist = Record<string, unknown> & {
  name?: string | null;
  avatarUrl?: string | null;
  image?: string | null;
  photo?: string | null;
};

export type SaleEntry = Record<string, unknown> & {
  id?: number | string | null;
  saleNumber?: number | string | null;
  bookingId?: number | string | null;
  reservationType?: string | null;
  bookingTypeName?: string | null;
  isOnline?: boolean | null;
  customerId?: number | string | null;
  customer?: { name?: string | null; surname?: string | null } | null;
  specialistName?: string | null;
  therapistName?: string | null;
  therapist?: SaleTherapist | null;
  serviceName?: string | null;
  classTitle?: string | null;
  description?: string | null;
  priceLabel?: string | null;
  startDisplay?: string | null;
  date?: string | null;
  slot?: SaleSlot | null;
  durationLabel?: string | null;
  pending?: number | string | null;
  totalWithDiscount?: number | string | null;
  tenantName?: string | null;
  tenantUrl?: string | null;
};

export type CheckoutContext = Record<string, unknown> & {
  sale?: SaleEntry | null;
  tenantName?: string | null;
  tenantUrl?: string | null;
  specialistName?: string | null;
  therapist?: SaleTherapist | null;
  type?: string | null;
  isOnline?: boolean | null;
  durationLabel?: string | null;
  slot?: SaleSlot | null;
};

export type CartState = Record<string, unknown> & {
  sales?: SaleEntry[] | null;
};

export type PaymentMethod = Record<string, unknown> & {
  id?: number | string | null;
  type?: number | string | null;
  name?: string | null;
  allowTokenization?: boolean | null;
  forceSaveCreditCard?: boolean | null;
};

export type SaleDepositInfo = {
  saleId?: number | string | null;
  depositAmount?: number | null;
};

export type CheckoutInfoData = Record<string, unknown> & {
  paymentMethods?: PaymentMethod[] | null;
  currency?: string | null;
  email?: string | null;
  name?: string | null;
  surname?: string | null;
  netPayPublicKey?: string | null;
  netPayProdMode?: boolean | null;
  payPalId?: string | null;
  deposits?: SaleDepositInfo[] | null;
  hasActivePromoCodes?: boolean | null;
};

export type CheckoutInfoResponse = Record<string, unknown> & {
  ok?: boolean;
  errorMsg?: string | null;
  model?: Record<string, unknown> | null;
  data?: CheckoutInfoData | null;
};

export type MercadoPagoConfig = {
  requestId: number;
  amount: number;
  currency: string;
  email: string;
  publicKey: string;
  endpoint?: string | null;
  payer?: {
    firstName?: string;
    lastName?: string;
  };
};

export type StripeConfig = {
  clientSecret: string;
  publicKey: string;
};

export type StripePaymentElement = {
  mount: (selector: string) => void;
  destroy: () => void;
};

export type StripeElements = {
  create: (type: string, options?: Record<string, unknown>) => StripePaymentElement;
};

export type StripeConfirmResult = {
  error?: { message?: string };
  paymentIntent?: { id?: string; client_secret?: string };
};

export type StripeInstance = {
  elements: (options: { appearance: Record<string, unknown>; clientSecret: string }) => StripeElements;
  confirmPayment: (options: { elements: StripeElements; confirmParams: { return_url: string } }) => Promise<StripeConfirmResult>;
};

export type StripeConstructor = (publicKey: string) => StripeInstance;

export type NetPayCardPayload = {
  cardNumber: string;
  expMonth: string;
  expYear: string;
  cvv2: string;
  cardHolderName: string;
};

export type NetPayTokenResponse = Record<string, unknown> & {
  message?: { data?: unknown };
};

export type NetPaySdk = {
  setApiKey: (key: string) => void;
  setSandboxMode: (sandbox: boolean) => void;
  token: {
    create: (
      payload: NetPayCardPayload,
      success: (response: NetPayTokenResponse) => void,
      failure: (error: { message?: string | null } | null) => void,
    ) => void;
  };
};

export type NetPayTokenResult = {
  tokenId: string | null;
  info: Record<string, unknown> | null;
  notPersisted: {
    card: string;
    exp: string;
  };
};

export type PaymentMethodExtras = {
  tokenId: string | null;
  tpvOnlineInfoTemp: string | null;
  tpvOnlineNotPersitedInfo: string | null;
};

export type PaymentFields = {
  acceptConditions: boolean;
  medicalConsent: boolean;
  marketingOptIn: boolean;
  saveCard?: boolean;
};

export type RedsysFormData = Record<string, unknown> & {
  ds_SignatureVersion?: string | null;
  Ds_SignatureVersion?: string | null;
  ds_MerchantParameters?: string | null;
  Ds_MerchantParameters?: string | null;
  ds_Signature?: string | null;
  Ds_Signature?: string | null;
};

export type RedsysPayload = Record<string, unknown> & {
  url?: string | null;
  data?: RedsysFormData | null;
};

export type WindowWithPaymentSdk = Window & {
  NetPay?: NetPaySdk;
  Stripe?: StripeConstructor;
};
